import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../Common/Configurations/AuthContext";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import Header from "../../Common/Layout/Header";
import {
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiEyeLine,
  RiSearchLine,
  RiFilterLine,
  RiRefreshLine,
  RiTimeLine,
  RiCalendarLine,
  RiUserLine,
  RiPlaneLine,
  RiBuildingLine,
  RiArrowRightLine,
  RiAlertLine,
  RiFileListLine,
  RiLoader4Line,
  RiShoppingCartLine,
  RiHistoryLine,
  RiDownloadLine,
  RiPrinterLine,
  RiMoneyDollarCircleLine,
  RiLockLine,
  RiTimerLine,
  RiCheckLine,
  RiCloseLine
} from "react-icons/ri";

const AgentBookings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Check for success message from block confirmation
  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
      // Clear state to prevent showing again on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    fetchMyBookings();
  }, []);

  useEffect(() => {
    filterBookings();
  }, [bookings, searchTerm, statusFilter]);

  const fetchMyBookings = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAll(
        `${config.GETAGENTBOOKINGS}/${user?.cid}`
      );
      
      if (response && response.success) {
        setBookings(response.bookings || []);
      } else {
        setBookings([]);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setMessage({ type: "error", text: "Failed to load bookings" });
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const filterBookings = () => {
    let filtered = [...bookings];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(booking =>
        booking.bookingId?.toLowerCase().includes(term) ||
        booking.seriesFareId?.toLowerCase().includes(term) ||
        booking.airlineCode?.toLowerCase().includes(term) ||
        booking.from?.toLowerCase().includes(term) ||
        booking.to?.toLowerCase().includes(term)
      );
    }

    // Apply status filter
    if (statusFilter !== "ALL") {
      filtered = filtered.filter(booking => booking.bookingStatus === statusFilter);
    }

    setFilteredBookings(filtered);
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setShowDetailsModal(true);
  };

  const handleMakePayment = (booking) => {
    // Navigate to payment screen
    navigate("/seriesfare-payment", {
      state: {
        fare: {
          seriesFareId: booking.seriesFareId,
          airlineCode: booking.airlineCode,
          from: booking.from,
          to: booking.to,
          departureDate: booking.travelDate,
          pricePerPerson: booking.pricePerSeat,
          companyName: booking.companyName,
          id: booking.id // MongoDB ID if needed
        },
        bookingPax: booking.paxCount,
        bookingId: booking.bookingId,
        bookingStatus: booking.bookingStatus
      }
    });
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to cancel this booking?")) {
      return;
    }

    try {
      setProcessing(true);
      const response = await apiService.post(
        config.SERIESBOOKINGCANCEL,
        { bookingId }
      );

      if (response && response.success) {
        setMessage({ type: "success", text: "Booking cancelled successfully" });
        fetchMyBookings();
      } else {
        throw new Error(response?.message || "Failed to cancel booking");
      }
    } catch (error) {
      console.error("Error cancelling booking:", error);
      setMessage({ type: "error", text: error.message || "Failed to cancel booking" });
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `INR ${Math.round(num).toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "BLOCK REQUEST":
        return { 
          class: "bg-warning", 
          icon: <RiTimeLine />, 
          text: "BLOCK REQUEST",
          description: "Waiting for DMC confirmation"
        };
      case "BLOCKED":
        return { 
          class: "bg-success", 
          icon: <RiCheckboxCircleLine />, 
          text: "BLOCKED",
          description: "Seats blocked, ready for payment"
        };
      case "CONFIRMED":
        return { 
          class: "bg-primary", 
          icon: <RiCheckboxCircleLine />, 
          text: "CONFIRMED",
          description: "Booking confirmed"
        };
      case "REJECTED":
        return { 
          class: "bg-danger", 
          icon: <RiCloseCircleLine />, 
          text: "REJECTED",
          description: "Block request rejected"
        };
      case "CANCELLED":
        return { 
          class: "bg-secondary", 
          icon: <RiCloseCircleLine />, 
          text: "CANCELLED",
          description: "Booking cancelled"
        };
      default:
        return { 
          class: "bg-light text-dark", 
          icon: <RiAlertLine />, 
          text: status,
          description: ""
        };
    }
  };

  const getPaymentStatusBadge = (status) => {
    switch (status) {
      case "SUCCESS":
        return { class: "bg-success", text: "PAID" };
      case "PENDING":
        return { class: "bg-warning", text: "PENDING" };
      case "FAILED":
        return { class: "bg-danger", text: "FAILED" };
      default:
        return { class: "bg-secondary", text: status };
    }
  };

  const refreshData = () => {
    setMessage({ type: "info", text: "Refreshing data..." });
    fetchMyBookings();
  };

  const getActionButton = (booking) => {
    if (booking.bookingStatus === "BLOCKED" && booking.paymentStatus === "PENDING") {
      return (
        <button
          className="btn btn-sm btn-success"
          onClick={() => handleMakePayment(booking)}
          disabled={processing}
          title="Make Payment"
        >
          <RiShoppingCartLine className="me-1" />
          Pay Now
        </button>
      );
    } else if (booking.bookingStatus === "BLOCK REQUEST") {
      return (
        <button
          className="btn btn-sm btn-danger"
          onClick={() => handleCancelBooking(booking.bookingId)}
          disabled={processing}
          title="Cancel Block Request"
        >
          <RiCloseLine className="me-1" />
          Cancel
        </button>
      );
    } else if (booking.bookingStatus === "CONFIRMED") {
      return (
        <button className="btn btn-sm btn-outline-primary" title="Download Invoice">
          <RiDownloadLine className="me-1" />
          Invoice
        </button>
      );
    }
    return null;
  };

  const handleNewBooking = () => {
    navigate("/flight/search");
  };

  return (
    <div id="layout-wrapper">
      <Header />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* Page Header */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 className="mb-1">My Series Fare Bookings</h3>
                    <p className="text-muted mb-0">View and manage your bookings</p>
                  </div>
                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-primary"
                      onClick={handleNewBooking}
                    >
                      <RiPlaneLine className="me-1" />
                      New Booking
                    </button>
                    <button 
                      className="btn btn-outline-secondary"
                      onClick={refreshData}
                      disabled={loading}
                    >
                      <RiRefreshLine className={`me-1 ${loading ? 'spinning' : ''}`} />
                      Refresh
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Message Alert */}
            {message.text && (
              <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
                {message.type === "success" ? <RiCheckLine className="me-2" /> : 
                 message.type === "error" ? <RiCloseLine className="me-2" /> : 
                 <RiLoader4Line className="me-2 spinning" />}
                {message.text}
                <button type="button" className="btn-close" onClick={() => setMessage({ type: "", text: "" })}></button>
              </div>
            )}

            {/* Filters */}
            <div className="row mb-4">
              <div className="col-md-8">
                <div className="input-group">
                  <span className="input-group-text">
                    <RiSearchLine />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by Booking ID, Airline, Route..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button className="btn btn-outline-secondary" type="button">
                    <RiFilterLine />
                  </button>
                </div>
              </div>
              <div className="col-md-4">
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="ALL">All Status</option>
                  <option value="BLOCK REQUEST">Block Request</option>
                  <option value="BLOCKED">Blocked</option>
                  <option value="CONFIRMED">Confirmed</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="row mb-4">
              <div className="col-md-2">
                <div className="card bg-warning bg-opacity-10 border-warning">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-warning">Pending</h6>
                        <h3 className="mb-0">
                          {bookings.filter(b => b.bookingStatus === "BLOCK REQUEST").length}
                        </h3>
                      </div>
                      <RiTimeLine className="text-warning" size={24} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-success bg-opacity-10 border-success">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-success">Ready to Pay</h6>
                        <h3 className="mb-0">
                          {bookings.filter(b => 
                            b.bookingStatus === "BLOCKED" && 
                            b.paymentStatus === "PENDING"
                          ).length}
                        </h3>
                      </div>
                      <RiLockLine className="text-success" size={24} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-primary bg-opacity-10 border-primary">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-primary">Confirmed</h6>
                        <h3 className="mb-0">
                          {bookings.filter(b => b.bookingStatus === "CONFIRMED").length}
                        </h3>
                      </div>
                      <RiCheckboxCircleLine className="text-primary" size={24} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-danger bg-opacity-10 border-danger">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-danger">Rejected</h6>
                        <h3 className="mb-0">
                          {bookings.filter(b => b.bookingStatus === "REJECTED").length}
                        </h3>
                      </div>
                      <RiCloseCircleLine className="text-danger" size={24} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-secondary bg-opacity-10 border-secondary">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-secondary">Cancelled</h6>
                        <h3 className="mb-0">
                          {bookings.filter(b => b.bookingStatus === "CANCELLED").length}
                        </h3>
                      </div>
                      <RiCloseLine className="text-secondary" size={24} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-info bg-opacity-10 border-info">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-info">Total</h6>
                        <h3 className="mb-0">{bookings.length}</h3>
                      </div>
                      <RiHistoryLine className="text-info" size={24} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bookings Table */}
            <div className="card">
              <div className="card-body">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading your bookings...</p>
                  </div>
                ) : filteredBookings.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="display-1 text-muted mb-3">
                      <RiFileListLine />
                    </div>
                    <h5>No Bookings Found</h5>
                    <p className="text-muted">
                      {bookings.length === 0 
                        ? "You haven't made any bookings yet. Start by searching for series fares."
                        : "No bookings match your search criteria."}
                    </p>
                    <button 
                      className="btn btn-primary mt-3"
                      onClick={handleNewBooking}
                    >
                      <RiPlaneLine className="me-2" />
                      Search Series Fares
                    </button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th width="120">Booking ID</th>
                          <th width="150">Flight Details</th>
                          <th width="80">Pax</th>
                          <th width="120">Amount</th>
                          <th width="150">Booking Status</th>
                          <th width="100">Payment</th>
                          <th width="150">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBookings.map((booking) => {
                          const statusBadge = getStatusBadge(booking.bookingStatus);
                          const paymentBadge = getPaymentStatusBadge(booking.paymentStatus);
                          
                          return (
                            <tr key={booking.bookingId} 
                                className={booking.bookingStatus === "BLOCKED" ? "table-success" :
                                         booking.bookingStatus === "BLOCK REQUEST" ? "table-warning" : ""}>
                              <td>
                                <div className="fw-bold small">
                                  {booking.bookingId?.substring(0, 8)}...
                                </div>
                                <div className="small text-muted">
                                  {formatDate(booking.createdAt)}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2"
                                       style={{width: '30px', height: '30px'}}>
                                    <RiPlaneLine size={14} />
                                  </div>
                                  <div>
                                    <div className="fw-bold">
                                      {booking.from} → {booking.to}
                                    </div>
                                    <div className="small text-muted">
                                      {booking.airlineCode} • {formatDate(booking.travelDate)}
                                    </div>
                                    {booking.companyName && (
                                      <div className="small text-muted">
                                        <RiBuildingLine className="me-1" />
                                        {booking.companyName}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div className="text-center">
                                  <h5 className="mb-0">{booking.paxCount}</h5>
                                  <small className="text-muted">Pax</small>
                                </div>
                              </td>
                              <td>
                                <div className="text-end">
                                  <div className="fw-bold text-success">
                                    {formatCurrency(booking.totalAmount)}
                                  </div>
                                  <div className="small text-muted">
                                    {formatCurrency(booking.pricePerSeat)} each
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex flex-column">
                                  <span className={`badge ${statusBadge.class} mb-1 d-inline-flex align-items-center gap-1`}>
                                    {statusBadge.icon}
                                    {statusBadge.text}
                                  </span>
                                  <small className="text-muted">
                                    {statusBadge.description}
                                  </small>
                                </div>
                              </td>
                              <td>
                                <span className={`badge ${paymentBadge.class}`}>
                                  {paymentBadge.text}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleViewDetails(booking)}
                                    title="View Details"
                                  >
                                    <RiEyeLine />
                                  </button>
                                  
                                  {getActionButton(booking)}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedBooking && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Booking Details</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDetailsModal(false)}
                ></button>
              </div>
              
              <div className="modal-body">
                {/* Status Banner */}
                <div className={`alert ${
                  selectedBooking.bookingStatus === "BLOCKED" ? "alert-success" :
                  selectedBooking.bookingStatus === "BLOCK REQUEST" ? "alert-warning" :
                  selectedBooking.bookingStatus === "CONFIRMED" ? "alert-primary" :
                  selectedBooking.bookingStatus === "REJECTED" ? "alert-danger" :
                  "alert-secondary"
                }`}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h5 className="alert-heading mb-1">
                        {getStatusBadge(selectedBooking.bookingStatus).text}
                      </h5>
                      <p className="mb-0">
                        {getStatusBadge(selectedBooking.bookingStatus).description}
                      </p>
                    </div>
                    {selectedBooking.bookingStatus === "BLOCKED" && (
                      <button
                        className="btn btn-success"
                        onClick={() => {
                          handleMakePayment(selectedBooking);
                          setShowDetailsModal(false);
                        }}
                      >
                        <RiShoppingCartLine className="me-2" />
                        Make Payment
                      </button>
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="card mb-3">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Booking Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="mb-2">
                          <small className="text-muted">Booking ID</small>
                          <div className="fw-bold">{selectedBooking.bookingId}</div>
                        </div>
                        <div className="mb-2">
                          <small className="text-muted">Fare ID</small>
                          <div className="fw-bold">{selectedBooking.seriesFareId}</div>
                        </div>
                        <div className="mb-2">
                          <small className="text-muted">Booking Dates</small>
                          <div>
                            <div>Created: {new Date(selectedBooking.createdAt).toLocaleString()}</div>
                            <div>Updated: {new Date(selectedBooking.updatedAt).toLocaleString()}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="card mb-3">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Status Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-6">
                            <div className="mb-2">
                              <small className="text-muted">Booking Status</small>
                              <div>
                                <span className={`badge ${getStatusBadge(selectedBooking.bookingStatus).class}`}>
                                  {selectedBooking.bookingStatus}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="mb-2">
                              <small className="text-muted">Payment Status</small>
                              <div>
                                <span className={`badge ${getPaymentStatusBadge(selectedBooking.paymentStatus).class}`}>
                                  {selectedBooking.paymentStatus}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Flight Details */}
                <div className="card mb-3">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">Flight Details</h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4">
                        <div className="mb-3">
                          <small className="text-muted">Route</small>
                          <h5>
                            <RiPlaneLine className="me-2" />
                            {selectedBooking.from} → {selectedBooking.to}
                          </h5>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted">Airline</small>
                          <p className="fw-bold">{selectedBooking.airlineCode}</p>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-3">
                          <small className="text-muted">Travel Date</small>
                          <p className="fw-bold">
                            <RiCalendarLine className="me-2" />
                            {formatDate(selectedBooking.travelDate)}
                          </p>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted">DMC Provider</small>
                          <p className="fw-bold">
                            <RiBuildingLine className="me-2" />
                            {selectedBooking.companyName || "Not specified"}
                          </p>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-3">
                          <small className="text-muted">Passengers</small>
                          <h4>
                            <RiUserLine className="me-2" />
                            {selectedBooking.paxCount}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Details */}
                <div className="card">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">Payment Details</h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <small className="text-muted">Price per Seat</small>
                          <h5 className="text-success">
                            {formatCurrency(selectedBooking.pricePerSeat)}
                          </h5>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted">Number of Passengers</small>
                          <h5>{selectedBooking.paxCount}</h5>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <small className="text-muted">Total Amount</small>
                          <h2 className="text-success">
                            {formatCurrency(selectedBooking.totalAmount)}
                          </h2>
                        </div>
                      </div>
                    </div>
                    
                    {selectedBooking.bookingStatus === "BLOCKED" && (
                      <div className="alert alert-warning">
                        <RiAlertLine className="me-2" />
                        <strong>Payment Required:</strong> Your seats are blocked. Please complete the payment 
                        within 24 hours to confirm your booking.
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="modal-footer">
                <div className="d-flex gap-2">
                  {selectedBooking.bookingStatus === "BLOCKED" && (
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={() => {
                        handleMakePayment(selectedBooking);
                        setShowDetailsModal(false);
                      }}
                    >
                      <RiShoppingCartLine className="me-2" />
                      Make Payment
                    </button>
                  )}
                  
                  {selectedBooking.bookingStatus === "BLOCK REQUEST" && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        if (window.confirm("Are you sure you want to cancel this block request?")) {
                          handleCancelBooking(selectedBooking.bookingId);
                          setShowDetailsModal(false);
                        }
                      }}
                    >
                      Cancel Request
                    </button>
                  )}
                  
                  {selectedBooking.bookingStatus === "CONFIRMED" && (
                    <>
                      <button className="btn btn-outline-primary">
                        <RiDownloadLine className="me-2" />
                        Download Invoice
                      </button>
                      <button className="btn btn-outline-secondary">
                        <RiPrinterLine className="me-2" />
                        Print
                      </button>
                    </>
                  )}
                  
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowDetailsModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        .spinning {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AgentBookings;